import { authClient, getLocalSession } from '@app/icare/client/auth'
import { invalidateForRowsChanged } from '@app/icare/client/cacheInvalidation'
import { type ClientDatabase, DatabaseSchema } from '@app/icare/client/db/schema'
import { events, reconnectEvents } from '@app/icare/client/events'
import { localQueryClient } from '@app/icare/client/localQueryClient'
import { startQueryIndex } from '@app/icare/client/queryIndex'
import { SyncDataChangedEventSchema } from '@app/icare/events'
import type { AppRouter } from '@app/icare/router'
import type { ClientStatusTabStateType } from '@app/icare/server/db/schema/ClientStatus'
import { createSyncRuntime } from '@pkg/sync/client'
import { apiClient } from './api'
import { db, initializer } from './db'

let appRouter: AppRouter | undefined

export const sync = createSyncRuntime<ClientDatabase, ClientStatusTabStateType>({
  namespace: 'icare:sync',
  db,
  userId: async () => {
    const localSession = getLocalSession()
    if (localSession?.user?.id) return localSession.user.id

    const session = await authClient.getSession()
    if (session?.user?.id) return session.user.id

    throw new Error('Cannot save without an authenticated session')
  },
  initializer,
  schema: DatabaseSchema,
  getTabState: () => {
    const lastMatch = appRouter?.state?.matches?.[appRouter.state.matches.length - 1]
    return {
      url: appRouter?.state?.location?.href,
      visibility: document.visibilityState === 'visible' ? 'visible' : 'hidden',
      routeId: lastMatch?.routeId ?? 'unknown',
      params: lastMatch?.params ?? {},
    }
  },
  sync: (req, tabs) => apiClient.sync.sync({ ...req, tabs }),
})

events.on(SyncDataChangedEventSchema.type, _event => {
  void sync.sync('remote')
})

reconnectEvents.on('reconnect', () => {
  void sync.sync('remote')
})

startQueryIndex()

sync.events.on('sync.rows.changed', e => {
  invalidateForRowsChanged(localQueryClient, e)
})

let stopRouteSync: (() => void) | undefined

// Route sync fires at router creation, outside AuthGate. Syncing while
// unauthenticated 401s, and the 401 handler reloads the page — which recreates
// the router and re-fires this sync, looping forever. Gate every trigger on a
// live session so a post-signout reload lands cleanly on the login screen.
const isAuthenticated = () => Boolean(authClient.$store.atoms.session?.get()?.data)

export const startClientStatusRouteSync = (nextRouter: AppRouter) => {
  stopRouteSync?.()
  appRouter = nextRouter
  if (isAuthenticated()) void sync.notifyTabStateChanged()

  const unsubscribe = nextRouter.subscribe('onResolved', event => {
    if (event.hrefChanged && isAuthenticated()) void sync.notifyTabStateChanged()
  })
  const onVisibilityChange = () => {
    if (isAuthenticated()) void sync.notifyTabStateChanged()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  stopRouteSync = () => {
    unsubscribe()
    document.removeEventListener('visibilitychange', onVisibilityChange)
    stopRouteSync = undefined
  }
}
